import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

export const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenGroup(null);
  }, [location.pathname]);

  const hormonalHealth = t("hormonalHealth.items", { returnObjects: true }) || [];
  const fertilityPregnancy = t("fertilityPregnancy.items", { returnObjects: true }) || [];
  const menopauseAging = t("menopauseAging.items", { returnObjects: true }) || [];
  const preventiveMedicine = t("preventiveMedicine.items", { returnObjects: true }) || [];

  const groups = [
    { label: t("nav.hormonalHealth"), base: "/hormonal-health", items: hormonalHealth },
    { label: t("nav.fertilityPregnancy"), base: "/fertility-pregnancy", items: fertilityPregnancy },
    { label: t("nav.menopauseAging"), base: "/menopause-aging", items: menopauseAging },
    { label: t("nav.preventiveMedicine"), base: "/preventive-medicine", items: preventiveMedicine },
  ];

  const simpleLinks = [
    { to: "/about", label: t("nav.about") },
    { to: "/formulare", label: t("nav.formulare") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full border-b bg-warm-ivory transition-shadow ${
        scrolled ? "border-border shadow-[0_8px_30px_rgba(26,37,48,0.05)]" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4 px-5 py-4 lg:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="HAYAT home" className="flex-shrink-0">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary">
          {groups.map((g) => (
            <div key={g.base} className="group relative">
              <Link
                to={g.base}
                data-testid={`nav-group-${g.base.slice(1)}`}
                className="flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium text-deep-ink/80 transition-colors hover:text-champagne-gold"
              >
                {g.label}
                <ChevronDown className="h-3.5 w-3.5 opacity-60 transition-transform group-hover:rotate-180" />
              </Link>
              <div className="invisible absolute start-0 top-full w-72 translate-y-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                <div className="mt-2 rounded-2xl border border-border bg-warm-ivory p-2 shadow-[0_20px_60px_rgba(26,37,48,0.12)]">
                  {g.items.map((it) => (
                    <Link
                      key={it.slug}
                      to={`${g.base}/${it.slug}`}
                      data-testid={`nav-item-${it.slug}`}
                      className="block rounded-xl px-4 py-2.5 text-sm text-deep-ink/80 transition-colors hover:bg-surface-hover hover:text-deep-ink"
                    >
                      {it.title}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {simpleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium transition-colors hover:text-champagne-gold ${
                  isActive ? "text-champagne-gold" : "text-deep-ink/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button
            asChild
            data-testid="header-book-button"
            className="hidden rounded-full bg-deep-ink px-5 text-warm-ivory transition-colors hover:bg-champagne-gold hover:text-deep-ink md:inline-flex"
          >
            <Link to="/book">{t("nav.bookConsultation")}</Link>
          </Button>

          {/* Mobile */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label={t("nav.menu")}
                className="inline-flex items-center justify-center rounded-full p-2 text-deep-ink xl:hidden"
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="end" className="w-[88vw] max-w-sm overflow-y-auto border-border bg-warm-ivory p-0">
              <SheetTitle className="sr-only">HAYAT</SheetTitle>
              <SheetDescription className="sr-only">{t("nav.menu")}</SheetDescription>
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <Logo />
                <button onClick={() => setOpen(false)} aria-label={t("nav.close")} data-testid="mobile-menu-close">
                  <X className="h-6 w-6 text-deep-ink" />
                </button>
              </div>
              <div className="flex flex-col px-3 py-4">
                {groups.map((g) => (
                  <div key={g.base} className="border-b border-border/60">
                    <button
                      data-testid={`mobile-group-${g.base.slice(1)}`}
                      onClick={() => setOpenGroup(openGroup === g.base ? null : g.base)}
                      className="flex w-full items-center justify-between px-2 py-3 text-start font-serif text-lg text-deep-ink"
                    >
                      {g.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${openGroup === g.base ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === g.base && (
                      <div className="pb-2">
                        <Link to={g.base} data-testid={`mobile-overview-${g.base.slice(1)}`} onClick={() => setOpen(false)} className="block rounded-lg px-4 py-2 text-sm text-champagne-gold">
                          {t("common.overview")}
                        </Link>
                        {g.items.map((it) => (
                          <Link
                            key={it.slug}
                            to={`${g.base}/${it.slug}`}
                            data-testid={`mobile-nav-item-${it.slug}`}
                            onClick={() => setOpen(false)}
                            className="block rounded-lg px-4 py-2 text-sm text-deep-ink/80"
                          >
                            {it.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {simpleLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-${l.to.slice(1)}`}
                    onClick={() => setOpen(false)}
                    className="border-b border-border/60 px-2 py-3 font-serif text-lg text-deep-ink"
                  >
                    {l.label}
                  </Link>
                ))}
                <Button
                  asChild
                  data-testid="mobile-book-button"
                  className="mt-5 rounded-full bg-deep-ink text-warm-ivory hover:bg-champagne-gold hover:text-deep-ink"
                >
                  <Link to="/book">{t("nav.bookConsultation")}</Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
